import { useContext, useEffect, useState } from "react";
import Container from "react-bootstrap/Container";
import FilterSection from "./FilterSection";
import {
  getUserNotRequests,
  setSelectedUser,
} from "../../../slices/users/userManagementSlice";
import { deleteClient } from "../../../slices/clients/clientManagementSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../../slices/store";
import useRequestApprovedUsers from "../../../hooks/useRequestApprovedUsers";
import useUserRole from "../../../hooks/useUserRole";
import UseAuthConfig from "../../../hooks/useAuthConfig";
import { TodosContext } from "../../../context/alert";
import { UserRole } from "../../../utils";
import { scrollToTop } from "../../../utils/helpers/helperMethods/scrollToTop";
import {
  getUserRoleFilterOptions,
  USER_ROLE_LABELS,
  userStatusData,
} from "./constants";
import { Link, useLocation } from "react-router-dom";
import { IUserResponse } from "../../../app/common-models/IUserResponse";
import BreadCrumb from "../../../components/organisms/Breadcrumb/BreadCrumb";
import Headline from "../../../components/organisms/Headline/Headline";
import FullWidthCard, {
  CardContentItem,
} from "../../../components/organisms/Cards/full-width-card/FullWidthCard";
import cardContent from "../../../utils/helpers/helperDataset/user-management-contents/cardContent.json";
import SearchHelper from "../../../components/organisms/Cards/user-card-controller/SearchHelper";
import useDebouncedDispatch from "../../../utils/DebouncedDispatch";
import Button from "../../../components/atoms/Buttons/Button/Button";
import Pagination from "../../../components/molecules/Pagination/Pagination";

const getInitials = (firstName?: string, lastName?: string) =>
  `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase();

// Shapes a user into the flat fields FullWidthCard renders.
const toCardData = (user: any) => ({
  ...user,
  role: USER_ROLE_LABELS[user.type] ?? user.type,
  organisation: user.organizationName,
});

const ManageUsers = () => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const existingUsers = useSelector(
    (state: RootState) => state.userManagement.users
  );
  const status = useSelector((state: RootState) => state.userManagement.status);
  const [userData, setUserData] = useState<IUserResponse>(existingUsers);
  const [clickFilterStatus, setClickFilterStatus] = useState(false);
  const [clickFilterSearch, setClickFilterSearch] = useState(false);
  const [paginationChanged, setPaginationChanged] = useState(false);
  const [usersRequests, updateSettings] = useRequestApprovedUsers();
  const [activePage, setActivePage] = useState(usersRequests.PageNumber);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<any>({
    value: 1,
    label: "All",
  });
  const isGlobalAdmin = useUserRole() === UserRole.GlobalAdmin;
  const roleOptions = getUserRoleFilterOptions(isGlobalAdmin);
  const [selectedRole, setSelectedRole] = useState<any>(roleOptions[0]);
  const { protectedResources }: any = UseAuthConfig();
  const { addAlert } = useContext(TodosContext);

  const debouncedDispatch = useDebouncedDispatch(dispatch);

  useEffect(() => {
    if (usersRequests) {
      dispatch(getUserNotRequests(usersRequests));
    }

    localStorage.setItem("userLocation", "/user/users");
    const storedOption = JSON.parse(
      localStorage.getItem("selectedOption") || "null"
    );
    if (storedOption) setSelectedOption(storedOption);

    const activePageInLocal = localStorage.getItem("usersActivePage");

    if (activePageInLocal) {
      setActivePage(parseInt(activePageInLocal));
      updateSettings({ PageNumber: parseInt(activePageInLocal) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setUserData(existingUsers);
    setIsLoading(false);
  }, [existingUsers]);

  useEffect(() => {
    localStorage.setItem("usersActivePage", activePage.toString());
  }, [activePage]);

  useEffect(() => {
    if (clickFilterStatus || clickFilterSearch || paginationChanged) {
      setIsLoading(true);
      debouncedDispatch(getUserNotRequests, usersRequests);
    }

    return () => {
      debouncedDispatch.cancel();
    };
  }, [
    usersRequests,
    clickFilterStatus,
    clickFilterSearch,
    paginationChanged,
    debouncedDispatch,
  ]);

  useEffect(() => {
    const previousLocation = localStorage.getItem("previousLocation");
    if (previousLocation === "/user/requests/view-profile/:id") {
      setIsLoading(true);
      dispatch(getUserNotRequests(usersRequests));
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const handleChangeSelector = (selected: any) => {
    localStorage.setItem("selectedOption", JSON.stringify(selected));
    setSelectedOption(selected);
    handleSetFilterByStatus(selected.label);
  };

  const handleSetFilterByStatus = (data: any) => {
    setClickFilterStatus(true);
    setClickFilterSearch(false);
    updateSettings({
      ParameterThree: data === "All" ? "Active,Pending,Inactive" : data,
    });
  };

  const handleChangeRole = (selected: any) => {
    setSelectedRole(selected);
    setClickFilterStatus(true);
    setClickFilterSearch(false);
    setActivePage(1);
    updateSettings({ ParameterTwo: selected.value, PageNumber: 1 });
  };

  const handleDeleteUser = (user: any) => {
    const params = {
      id: user.id,
      accessToken: localStorage.getItem("AccessToken") || "",
      url: `${protectedResources.apiTodoList.endpoint}/users/${user.id}`,
    };
    setIsLoading(true);
    scrollToTop();
    dispatch(deleteClient(params)).then((r: any) => {
      if (r?.payload?.success === true) {
        addAlert("User Deleted Successfully", "success", true);
        dispatch(getUserNotRequests(usersRequests));
      } else {
        setIsLoading(false);
        addAlert("User Delete Failed", "failed", true);
      }
    });
  };

  const handleSetFilteredValue = (inputValue: string) => {
    setClickFilterStatus(false);
    setClickFilterSearch(true);
    updateSettings({ SearchText: inputValue });
  };

  const handlePageChange = ({ selected }: { selected: number }) => {
    setPaginationChanged(true);
    setIsLoading(true);
    setActivePage(selected + 1);
    updateSettings({ PageNumber: selected + 1 });
  };

  return (
    <Container fluid className="layout-bar">
      <BreadCrumb />

      <Headline name="Manage users" subHeading="Add, edit, and manage users seamlessly" />

      <div className="content-wrapper">
        <div className="filter-wrapper">
          <FilterSection
            userStatusData={userStatusData}
            handleSetFilteredValue={handleSetFilteredValue}
            handleSetFilterByStatus={handleSetFilterByStatus}
            selectedOption={selectedOption}
            handleChangeSelector={handleChangeSelector}
            secondaryOptions={roleOptions}
            selectedSecondaryOption={selectedRole}
            handleChangeSecondarySelector={handleChangeRole}
          />

          <Link to="/user/add-user">
            <Button cssClass="btn-primary" text="Add New User" />
          </Link>
        </div>
      </div>

      <div className="content-wrapper">
        <div>
          {userData?.data?.length > 0 ? (
            userData.data.map((user: any) => (
              <div key={user.id} onClick={() => dispatch(setSelectedUser(user))}>
                <FullWidthCard
                  cardContent={cardContent as CardContentItem[]}
                  data={toCardData(user)}
                  title={`${user.firstName ?? ""} ${user.lastName ?? ""}`.trim()}
                  avatarText={getInitials(user.firstName, user.lastName)}
                  pathUrl="user/users/view-profile"
                  deleteCard={handleDeleteUser}
                />
              </div>
            ))
          ) : (
            <SearchHelper
              isLoading={isLoading}
              userdata={userData}
              filteredValue={[]}
              status={status}
            />
          )}
        </div>

        {userData?.totalPages > 1 && (
          <Pagination
            pageNumber={activePage}
            onPageChange={(page) => {
              setPaginationChanged(true);
              setIsLoading(true);
              setActivePage(page);
              updateSettings({ PageNumber: page });
            }}
            totalPages={userData?.totalPages}
            pageSize={usersRequests?.PageSize}
            totalRecords={userData?.totalRecords || 0}
            label="Users"
          />
        )}
      </div>
    </Container>
  );
};

export default ManageUsers;
